<template>
  <transition name="fade" appear>
    <div class="modal-content" v-if="isModalOpen">
      <span class="close" @click="closeModal()"> &times; </span>
      <div class="text--bold mb-10px" v-html="$i18n('cartRecuperationModalText')" />
      <div v-for="item in items.slice(0, MAX_ITEMS)" :key="item.id" class="flex item-card">
        <img class="img-mini-part" :src="getItemImgUrl(item)" alt="item" />
        <div class="flex flex-col">
          <div class="flex grid-gap-5px">
            <div class="text--bold">
              {{ item.Part3DPrintName }}
            </div>
            <div v-if="items.length > MAX_ITEMS">
              {{ $i18n('plusothersItems', { count: items.length - MAX_ITEMS }) }}
            </div>
          </div>
          <div>
            {{ referential.mapProcessFamilies[item.ProcessFamily]?.name }}
          </div>
          <div class="flex flex-row grid-gap-5px">
            <img
              class="other-img-mini-part"
              :src="getItemImgUrl(otherItems)"
              alt="item"
              v-for="otherItems in items.slice(MAX_ITEMS, items.length)"
              :key="otherItems.id"
            />
          </div>
        </div>
      </div>
      <div class="button-container">
        <VuBtn @click="continueQuote()" class="validation-button">
          <div class="text--bold">
            {{ $i18n('continueQuote') }}
            <div />
          </div>
        </VuBtn>
      </div>
    </div>
  </transition>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useTracker } from '~/composables/tracker';
import { useTranslations } from '~/composables/translations';

import VuBtn from '@ds/vuekit/src/components/vu-btn.vue';
import { useMeStore } from '~/stores/me';
import { useFileStore } from '~/stores/file';
import { useSettingsStore } from '~/stores/settings';
import { useReferentialStore } from '~/stores/referential';

const me = useMeStore();
const referential = useReferentialStore();
const file = useFileStore();
const settings = useSettingsStore();
const router = useRouter();
const tracker = useTracker();
const { $i18n } = useTranslations('homepage');

const isModalOpen = ref(false);
const draftOrder = ref([]);
const recentOrder = ref([]);
const lastRecentOrder = ref([]);
const items = ref([]);
const thumbnails = ref([]);
const baseUrl = ref('');
const MAX_ITEMS = 1;

const getFormatFromName = (name: string) => {
  if (!name) {
    return 'other';
  }

  const format = name.split('.')?.pop()?.toLowerCase();
  const normalFormat = ['stl', 'obj', 'dxf', 'dwg', 'catpart', 'sldprt', '3dxml'];

  switch (format) {
    case 'igs':
    case 'iges':
      return 'iges';
    case 'stp':
    case 'step':
      return 'step';
    default:
      return format ? (normalFormat.includes(format) ? format : 'other') : 'other';
  }
};

const defaultImageUrl = (item: any) =>
  `${baseUrl.value}MP3DPOrderListWidget/assets/images/${getFormatFromName(
    item.Part3DPrintName,
  )}.png`;

const getItemImgUrl = (item: any) =>
  thumbnails.value[item.id] ? thumbnails.value[item.id] : defaultImageUrl(item);

const fetchDraftOrders = async () => {
  draftOrder.value =
    (await me.fetchMyRequests({ start: 0, size: 10, state: 'draft' }))?.carts || [];
};

// Helper functions for managing dismissed IDs with 7-day expiration
const getDismissedIds = (): string[] => {
  const stored = localStorage.getItem('dismissed-draft-ids');
  if (!stored) {
    return [];
  }

  const dismissedData = JSON.parse(stored);
  const now = new Date().getTime();
  const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

  // Filter out entries older than 7 days
  const validEntries = dismissedData.filter((entry: any) => now - entry.timestamp < sevenDaysInMs);

  // Update localStorage with cleaned data
  if (validEntries.length !== dismissedData.length) {
    localStorage.setItem('dismissed-draft-ids', JSON.stringify(validEntries));
  }

  // Return just the IDs
  return validEntries.map((entry: any) => entry.id);
};

const addDismissedId = (id: string) => {
  const stored = localStorage.getItem('dismissed-draft-ids');
  const dismissedData = stored ? JSON.parse(stored) : [];

  // Check if ID already exists
  const exists = dismissedData.some((entry: any) => entry.id === id);

  if (!exists) {
    dismissedData.push({
      id,
      timestamp: new Date().getTime(),
    });
    localStorage.setItem('dismissed-draft-ids', JSON.stringify(dismissedData));
  }
};

const closeModal = () => {
  if (lastRecentOrder.value?.id) {
    addDismissedId(lastRecentOrder.value.id);
  }

  isModalOpen.value = false;
};

async function continueQuote() {
  tracker?.event('cart_recup_yes_click', 'display', 'CLICK');
  if (lastRecentOrder.value) {
    localStorage.setItem('vue-cart-id', lastRecentOrder.value.id);
    isModalOpen.value = false;
    await router.push({ name: 'QuoteEdition' });
  }
}

// Close modal after 20 seconds
const showCartModal = () => {
  isModalOpen.value = true;
  // Hide the modal after 20 seconds
  setTimeout(() => {
    isModalOpen.value = false;
    localStorage.setItem('recup-modal-timeout', 'true');
  }, 20000);
};

onMounted(async () => {
  await me.fetchMe();
  if (
    me.isLoggedIn &&
    !settings.isMobileDevice &&
    window.history.state.back === null &&
    localStorage.getItem('recup-modal-timeout') !== 'true'
  ) {
    baseUrl.value = window.dsDefaultWebappsBaseUrl;
    await fetchDraftOrders();
    await referential.fetchProcessFamilies();
    if (draftOrder.value && draftOrder.value.length > 0) {
      const now = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(now.getDate() - 7);

      // Filter recent orders (within 7 days)
      recentOrder.value = draftOrder.value.filter((order) => {
        const createdDate = new Date(order.Created);
        return createdDate >= sevenDaysAgo;
      });

      // Get dismissed IDs
      const dismissedIds = getDismissedIds();

      // Find the most recent order that hasn't been dismissed
      const nonDismissedOrders = recentOrder.value.filter(
        (order) => !dismissedIds.includes(order.id),
      );

      if (nonDismissedOrders.length > 0) {
        // Sort by creation date (most recent first) and get the first one
        lastRecentOrder.value = nonDismissedOrders.sort(
          (a, b) => new Date(b.Created).getTime() - new Date(a.Created).getTime(),
        )[0];

        items.value = lastRecentOrder.value.Items;
        thumbnails.value = await file.fetchThumbnails({ cart: lastRecentOrder.value });
        showCartModal();
      }
    }
  }
});
</script>

<style scoped>
.modal-content {
  z-index: 99;
  background-color: #f1f1f1;
  padding: 20px;
  border: 1px solid #888;
  width: 300px;
  min-height: 150px; /* Optional: minimum height */
  max-height: 80vh; /* Optional: maximum height to prevent overflow */
  overflow-y: auto; /* Only show scrollbar when content exceeds max-height */
  position: absolute;
  top: 95px;
  right: 20px;
  font-family: 'sans-serif';
}

.close {
  color: #aaa;
  top: 5px;
  right: 10px;
  position: absolute;
  font-size: 28px;
  font-weight: bold;
}

.close:hover,
.close:focus {
  color: black;
  text-decoration: none;
  cursor: pointer;
}

.button-container {
  display: flex;
  justify-content: space-evenly;
  margin-top: 20px;
}

.validation-button {
  color: white;
  background-color: var(--blue-2) !important;
  border-color: var(--blue-2);
  width: 60%;
}

.img-mini-part {
  width: 60px;
  height: 60px;
}

.other-img-mini-part {
  width: 20px;
  height: 20px;
}

.validation-button:hover {
  background-color: var(--blue-4) !important;
}

.item-card {
  border: 1px solid grey;
  border-radius: 8px;
  padding: 10px;
  margin: 5px 0;
  align-items: center; /* Center content vertically */
  gap: 10px; /* Add space between image and text */
  background-color: #fefefe;
}

.more-items-card {
  justify-content: center;
  height: 80px;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>
